import React, { useState, useCallback } from 'react';
import { Header } from '@/app/components/Header';
import { Footer } from '@/app/components/Footer';
import { AuthModal } from '@/app/components/AuthModal';

export function PrivacyPolicy() {
  const [showAuthModal, setShowAuthModal] = useState(false);

  const handleSignInNeeded = useCallback(() => {
    console.log('PrivacyPolicy: User needs to sign in');
    setShowAuthModal(true);
  }, []);

  const handleCloseAuthModal = useCallback(() => {
    setShowAuthModal(false);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50">
      <Header onSignInNeeded={handleSignInNeeded} />
      
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white/90 backdrop-blur-xl rounded-3xl p-8 lg:p-12 shadow-xl border border-white/50">
            <h1 className="text-4xl font-bold text-gray-900 mb-8">Privacy Policy</h1>
            <p className="text-gray-600 mb-8">Last updated: {new Date().toLocaleDateString()}</p>

            <div className="space-y-8 text-gray-700 leading-relaxed">
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Introduction</h2>
                <p>
                  Zaptopia ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains 
                  how we collect, use, and safeguard your information when you use our community-driven app discovery platform.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Information We Collect</h2>
                
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Account Information</h3>
                <p className="mb-4">
                  When you create an account, we collect basic information such as your email address and chosen username. 
                  We use secure authentication providers and don't store passwords directly.
                </p>

                <h3 className="text-lg font-semibold text-gray-800 mb-2">Content You Submit</h3>
                <p className="mb-4">
                  We collect and store the app requests you submit, votes you cast, and comments you make. 
                  This content is publicly visible as part of our community platform.
                </p>

                <h3 className="text-lg font-semibold text-gray-800 mb-2">Analytics and Usage Data</h3>
                <p>
                  To improve our service, we collect analytics data about how you use Zaptopia, including:
                </p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>Search queries and app discovery patterns</li>
                  <li>Page views and navigation behavior</li>
                  <li>Feature usage and user interactions</li>
                  <li>Performance and error monitoring</li>
                </ul>
                <p className="mt-4">
                  <strong>Important:</strong> We don't collect IP addresses or create detailed personal profiles. 
                  All analytics data is processed in the EU through our privacy-focused analytics provider (PostHog).
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">How We Use Your Information</h2>
                <ul className="list-disc list-inside space-y-2">
                  <li>Provide and improve our app discovery platform</li>
                  <li>Enable community features like voting and commenting</li>
                  <li>Understand user needs and popular app categories</li>
                  <li>Detect and prevent spam or abuse</li>
                  <li>Communicate important updates about the service</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data Processing and Storage</h2>
                <p className="mb-4">
                  Your data is processed and stored in the European Union to ensure high privacy standards. 
                  We use reputable service providers who comply with GDPR and other privacy regulations.
                </p>
                <p>
                  We retain your data only as long as necessary to provide our services or as required by law. 
                  You can request deletion of your account and associated data at any time.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Your Privacy Rights</h2>
                <p className="mb-4">Under GDPR and other privacy laws, you have the right to:</p>
                <ul className="list-disc list-inside space-y-2">
                  <li><strong>Access</strong> your personal data</li>
                  <li><strong>Correct</strong> inaccurate information</li>
                  <li><strong>Delete</strong> your account and data</li>
                  <li><strong>Export</strong> your data in a portable format</li>
                  <li><strong>Opt-out</strong> of analytics tracking</li>
                  <li><strong>Object</strong> to certain data processing</li>
                </ul>
                <p className="mt-4">
                  To exercise these rights, contact us at <strong>info@zapt.ai</strong>
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Analytics Opt-Out</h2>
                <p className="mb-4">
                  While we use analytics to improve Zaptopia, you can opt out at any time. Note that opting out 
                  won't affect your ability to use any features of the platform.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-blue-800">
                    To opt out of analytics, email us at <strong>info@zapt.ai</strong> with "Analytics Opt-Out" 
                    in the subject line, or look for the opt-out option in your account settings.
                  </p>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Cookies and Tracking</h2>
                <p>
                  We use minimal cookies necessary for the platform to function and for analytics purposes. 
                  We don't use advertising cookies or sell data to third parties.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Third-Party Services</h2>
                <p className="mb-4">We use the following third-party services:</p>
                <ul className="list-disc list-inside space-y-2">
                  <li><strong>PostHog (EU)</strong> - Privacy-focused analytics</li>
                  <li><strong>Authentication providers</strong> - Secure sign-in</li>
                  <li><strong>Cloud hosting</strong> - Reliable service delivery</li>
                </ul>
                <p className="mt-4">
                  All third-party services are chosen for their strong privacy practices and GDPR compliance.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data Security</h2>
                <p>
                  We implement appropriate technical and organizational measures to protect your data against 
                  unauthorized access, alteration, disclosure, or destruction. However, no internet transmission 
                  is 100% secure, so we can't guarantee absolute security.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Children's Privacy</h2>
                <p>
                  Zaptopia is not intended for children under 13. We don't knowingly collect personal information 
                  from children under 13. If you believe we have inadvertently collected such information, 
                  contact us immediately.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Changes to This Policy</h2>
                <p>
                  We may update this Privacy Policy occasionally. We'll notify users of significant changes 
                  via email or through the platform. Continued use of Zaptopia after changes constitutes 
                  acceptance of the updated policy.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Us</h2>
                <p>
                  If you have questions about this Privacy Policy or how we handle your data, please contact us:
                </p>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-4">
                  <p><strong>Email:</strong> info@zapt.ai</p>
                  <p><strong>Subject Line:</strong> Privacy Policy Question</p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </main>

      <Footer />

      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={handleCloseAuthModal} 
      />
    </div>
  );
} 